import { useRef, type PointerEvent } from 'react';
import { CardIcon } from './Icon';
import { cardArt } from '../game/art';
import { useT } from '../i18n';
import type { CardKind } from '../game/types';

/**
 * Fullscreen card viewer with a pointer-driven parallax tilt: the card rotates
 * toward the cursor/finger, the art layer drifts for depth, and a glare follows.
 * Works with mouse and touch (pointer events). Tap the backdrop to dismiss.
 * Used by the Card Codex and by long-pressing a card in hand at the table.
 */
export function CardDetail({
  kind,
  color,
  onClose,
}: {
  kind: CardKind;
  color: string;
  onClose: () => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);

  function tilt(e: PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5; // -0.5 … 0.5
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--rx', `${(-y * 18).toFixed(2)}deg`);
    el.style.setProperty('--ry', `${(x * 18).toFixed(2)}deg`);
    el.style.setProperty('--px', `${(-x * 16).toFixed(1)}px`);
    el.style.setProperty('--py', `${(-y * 16).toFixed(1)}px`);
    el.style.setProperty('--gx', `${(x * 90 + 50).toFixed(1)}%`);
    el.style.setProperty('--gy', `${(y * 90 + 50).toFixed(1)}%`);
  }
  function reset() {
    const el = ref.current;
    if (!el) return;
    for (const p of ['--rx', '--ry', '--px', '--py']) el.style.setProperty(p, '0');
    el.style.setProperty('--gx', '50%');
    el.style.setProperty('--gy', '40%');
  }

  return (
    <div className="cd-backdrop" onClick={onClose}>
      <div className="cd-stage" onClick={(e) => e.stopPropagation()}>
        <div
          ref={ref}
          className="cd-card"
          style={{ ['--accent' as string]: color }}
          onPointerMove={tilt}
          onPointerLeave={reset}
        >
          <span className="cd-art" style={{ backgroundImage: `url(${cardArt(kind)})` }} />
          <span className="cd-glare" aria-hidden />
          <span className="cd-mark"><CardIcon kind={kind} size={22} /></span>
          <div className="cd-copy">
            <div className="cd-name" style={{ color }}>{t.card.name[kind]}</div>
            <div className="cd-desc">{t.card.desc[kind]}</div>
          </div>
        </div>
        <button className="link cd-close" onClick={onClose}>{t.codex.close}</button>
      </div>
    </div>
  );
}
